<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  preference: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle-active']);

const { t } = useI18n();

const typeLabel = computed(() => {
  if (props.preference.field_type === 'list') {
    return t('CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.list');
  }
  if (props.preference.field_type === 'list_with_custom') {
    return t('CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.list_with_custom');
  }
  return t('CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.text');
});

const statusLabel = computed(() =>
  props.preference.active
    ? t('CONTACT_PREFERENCES_SETTINGS.STATUS.ACTIVE')
    : t('CONTACT_PREFERENCES_SETTINGS.STATUS.INACTIVE')
);
</script>

<template>
  <div
    class="flex flex-col py-4 min-w-0"
    :class="{ 'opacity-70': !preference.active }"
  >
    <div class="flex justify-between flex-row items-center gap-4 min-w-0">
      <div class="flex items-center gap-4 min-w-0">
        <div
          class="flex items-center flex-shrink-0 size-10 justify-center rounded-xl outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon icon="i-lucide-menu" class="size-4 text-n-slate-11" />
        </div>
        <div class="flex flex-col gap-1.5 items-start min-w-0 overflow-hidden">
          <div class="flex items-center gap-2 min-w-0">
            <h4 class="text-heading-3 truncate text-n-slate-12 min-w-0">
              {{ preference.label }}
            </h4>
            <div class="flex items-center gap-1.5">
              <Label :label="typeLabel" compact />
              <Label :label="statusLabel" compact />
            </div>
          </div>
          <div class="grid grid-cols-[auto_1fr] items-center gap-1.5">
            <Icon icon="i-lucide-key-round" class="size-3.5 text-n-slate-11" />
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-body-main text-n-slate-11 truncate">
                {{ preference.key }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-3 justify-end flex-shrink-0">
        <Switch
          :model-value="preference.active"
          :aria-label="
            preference.active
              ? t('CONTACT_PREFERENCES_SETTINGS.STATUS.DISABLE')
              : t('CONTACT_PREFERENCES_SETTINGS.STATUS.ENABLE')
          "
          @update:model-value="emit('toggle-active', preference)"
        />
        <Button
          icon="i-woot-edit-pen"
          slate
          sm
          @click="emit('edit', preference)"
        />
        <Button
          icon="i-woot-bin"
          slate
          sm
          class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          @click="emit('delete', preference)"
        />
      </div>
    </div>
  </div>
</template>
